import { Loader2 } from "lucide-react";
import { useEffect, type ReactNode } from "react";

import { restoreSession } from "@/lib/api";
import { useAuthInitialized, useAuthStore } from "@/stores/auth";

/**
 * Restores the session before the router renders.
 *
 * The access token lives only in memory, so after a reload the app has no
 * credentials at all. One call to /api/auth/refresh presents the HttpOnly
 * cookie and gets a fresh token back. Rendering the routes before that
 * resolves would bounce an authenticated user to the sign-in page.
 */
export function BootGate({ children }: { children: ReactNode }) {
  const initialized = useAuthInitialized();

  useEffect(() => {
    let cancelled = false;

    // A failed restore simply means nobody is signed in; the guards take it
    // from there. The request is de-duplicated, so React's double-invoked
    // effects in development do not rotate the refresh token twice.
    void restoreSession().finally(() => {
      if (!cancelled) useAuthStore.getState().markInitialized();
    });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!initialized) {
    return (
      <div className="flex min-h-dvh items-center justify-center" aria-busy="true">
        <Loader2 className="text-muted-foreground size-6 animate-spin" aria-label="Carregando" />
      </div>
    );
  }

  return children;
}
